import { PreferredScheduleConfig, QuestionOption, QuestionOptionEditModel } from '@models/product';
import { isCalendarOptionProduct } from './product-option-date';

export const generatePreferredScheduleTimeSlots = (config?: PreferredScheduleConfig): string[] => {
  if (!config?.startTime || !config?.endTime) {
    return [];
  }

  const parseMinutes = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    return hour * 60 + minute;
  };

  let current = parseMinutes(config.startTime);
  const end = parseMinutes(config.endTime);
  const interval = config.intervalMinutes || 30;
  const slots: string[] = [];

  while (current <= end) {
    const hour = Math.floor(current / 60);
    const minute = current % 60;
    slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    current += interval;
  }

  return slots;
};

export const groupPreferredScheduleSlotsByAmPm = (
  slots: string[],
): { am: string[]; pm: string[] } => {
  const am: string[] = [];
  const pm: string[] = [];

  slots.forEach((slot) => {
    const hour = Number(slot.split(':')[0]);
    if (hour < 12) {
      am.push(slot);
    } else {
      pm.push(slot);
    }
  });

  return { am, pm };
};

export const formatPreferredScheduleChipLabel = (time: string) => {
  const [hourText, minuteText] = time.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (hour === 0) {
    return `12:${String(minute).padStart(2, '0')}`;
  }
  if (hour <= 12) {
    return `${hour}:${String(minute).padStart(2, '0')}`;
  }
  return `${hour - 12}:${String(minute).padStart(2, '0')}`;
};

export const formatPreferredScheduleAnswer = (date: string, time: string) => `${date} ${time}`;

export const parsePreferredScheduleAnswer = (answer?: string): { date?: string; time?: string } => {
  if (!answer) {
    return {};
  }

  const match = answer.trim().match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})$/);
  if (!match) {
    return {};
  }

  return { date: match[1], time: match[2] };
};

export const isPreferredScheduleQuestionAnswered = (answer?: string) => {
  const { date, time } = parsePreferredScheduleAnswer(answer);
  return Boolean(date && time);
};

export const getPreferredScheduleQuestionOption = (questionOptions?: QuestionOption[]) => {
  return (questionOptions || []).find((option) => option.questionType === 'preferredSchedule');
};

/** 선택 옵션 step 라벨에 날짜·시간이 모두 있으면 희망 일정 설정 불가 */
export const hasDateAndTimeOptionLabels = (optionLabels?: string[]) => {
  const labels = optionLabels || [];
  return labels.includes('날짜') && labels.includes('시간');
};

export const shouldShowPreferredScheduleOnProductDetail = (
  questionOptions?: QuestionOption[],
  productOptions?: Parameters<typeof isCalendarOptionProduct>[0],
  forceOptionModal?: boolean,
  optionLabels?: string[],
) => {
  if (isCalendarOptionProduct(productOptions, { forceOptionModal })) {
    return false;
  }
  if (hasDateAndTimeOptionLabels(optionLabels)) {
    return false;
  }
  return Boolean(getPreferredScheduleQuestionOption(questionOptions));
};

/** CommerceOptionModal / CommercePayment 에서 사용 (희망 일정 제외) */
export const getModalQuestionOptions = (questionOptions?: QuestionOption[]) => {
  return (questionOptions || []).filter((option) => option.questionType !== 'preferredSchedule');
};

/** @deprecated use getModalQuestionOptions */
export const getActiveQuestionOptions = getModalQuestionOptions;

export const mergePreferredScheduleQnaOptions = (
  modalQnaOptions: QuestionOptionEditModel[],
  questionOptions?: QuestionOption[],
  preferredScheduleAnswer?: string,
) => {
  const preferredScheduleOption = getPreferredScheduleQuestionOption(questionOptions);
  if (!preferredScheduleOption || !preferredScheduleAnswer?.trim()) {
    return modalQnaOptions;
  }

  return [
    ...modalQnaOptions,
    {
      ...preferredScheduleOption,
      answer: preferredScheduleAnswer.trim(),
    },
  ];
};
